import { Row, Col, Radio, FormInstance } from "antd";
import { formatNumber } from "../../utils/textFormatting";
import { HomeOutlined, FileTextOutlined } from "@ant-design/icons";
import {
  Input,
  Form,
  AutoComplete,
  Checkbox,
  DatePicker,
  MaskedInput,
  InputNumber,
  Select,
} from "@dreambigger/design-system";
import CloudinaryUpload from "../CloudinaryUpload";
import {
  CustomFieldConfig,
  FinancialBrand,
  MembershipEligibility,
  ResponseFields,
} from "@dreambigger/shared/src/types";
import { DollarCircleOutlined } from "@ant-design/icons";
import SliderWithDisplay from "../SliderWithDisplay";
import styles from "../../steps/steps.module.scss";
import {
  isValidAutoComplete,
  isValidPhoneNumber,
  isValidSsn,
  isValidRoutingNumber,
  antdValidateRegex,
} from "../../utils/validators";
import { formatAsNationalPhoneNum } from "../../utils/textFormatting";
import { ReactElement, useCallback, useEffect, useState, useRef } from "react";
import { AddressAutocomplete, AddressFields } from "../AddressAutocomplete";
import { useFlow, useDisclosureLogs } from "../../api";
import { getAdminFinancialInstitutionId } from "../adminApplicationStore";
import dayjs from "dayjs";
import { convertFunctionStrings } from "../../utils/parsing";
import { getDate } from "../../utils/dayjs_helpers";
import SSNMaskedInput from "../SSNMaskedInput";
import PrivacyMaskedInput from "../PrivacyMaskedInput";
import disclosureDocumentUrl from "../../utils/disclosureDocumentUrl";

export const addressKeySuffix = "address";
export const apartmentKeySuffix = "apartment";
export const cityKeySuffix = "city";
export const stateKeySuffix = "state";
export const zipKeySuffix = "zip";

// Checkbox truthiness. Matches the disclosure decision helpers, which also accept
// the string "true" since older stored responses have it.
const isTruthy = (value: unknown): boolean =>
  value === true || value === "true";

export interface CustomFieldProps {
  field: CustomFieldConfig;
  index: number;
  form: FormInstance<any>;
  brand: FinancialBrand;
  nameSuffix?: string;
  readonly?: boolean;
  onChange?: (changedValues: ResponseFields, values: ResponseFields) => void;
  checkDisabled?: (values?: ResponseFields) => void;
  application?: any;
  /** Slug of the step this field is rendered on (used for disclosure acks). */
  stepSlug?: string;
}

export default function CustomField({
  field,
  index,
  form,
  brand,
  nameSuffix,
  onChange,
  readonly = false,
  checkDisabled,
  application,
  stepSlug,
}: CustomFieldProps) {
  const confirmationValidRef = useRef<boolean>(true);
  const FULL_WIDTH_COMPONENTS = [
    "textarea",
    "slider",
    "cloudinary",
    "radio",
    "checkbox",
    "disclosure",
    "multiselect",
  ];
  const [loaded, setLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Whether the applicant has opened the disclosure document at least once.
  // Gates the "I agree" checkbox when the field is configured with requireOpen.
  const [disclosureOpened, setDisclosureOpened] = useState(false);
  const SSN_ERROR_MESSAGE = "Please confirm your SSN";
  const { data: flow } = useFlow();
  // `useFlow` keys on `router.query.flowId`, which the admin-edit route
  // (/organization/[organizationSlug]/applications/[applicationId]) does not
  // have — so in admin mode `flow` is undefined, `useDisclosureLogs` falls back
  // to the default token namespace, finds no token, and drops every `viewed`
  // event on the floor. The store the admin wrapper populates knows the FI.
  //
  // Passed as a getter, not a value: that store is a module singleton filled in
  // the wrapper's effect, and effects run child-first, so reading it here during
  // render returns null on the render that matters.
  const { recordEvent: recordDisclosureEvent } = useDisclosureLogs(
    flow?.financialInstitution?.id,
    getAdminFinancialInstitutionId
  );

  // If a nameSuffix is provided, append it to the field name.
  const fieldName = nameSuffix
    ? `${field.fieldKey}__${nameSuffix}`
    : field.fieldKey;

  // Live value for this field. The disclosure case needs it to stay correct when
  // prefill lands after mount, so read it through a subscription rather than a
  // one-time form.getFieldValue during render.
  const currentValue = Form.useWatch(fieldName, form);

  // When the fieldName is assigned and the component has been rendered,
  // set the loaded state to true.
  useEffect(() => {
    if (!fieldName) return;
    setLoaded(true);
  }, [fieldName]);

  // After the field has been loaded, set its value to a defaultValue if
  // applicable and there is no previously-saved value.
  useEffect(() => {
    if (!loaded) {
      return;
    }
    const values = form.getFieldsValue();
    if (!values[fieldName] && "fieldDefaultValue" in field) {
      form.setFieldsValue({
        [fieldName]: field.fieldDefaultValue,
      });
    }
    // Seed a disclosure to an explicit `false` so an untouched box still submits
    // a value. step-wrapper drops `undefined` from the payload, and the API reads
    // key presence as "this disclosure was actually shown to the applicant", so a
    // missing key has to mean "never rendered" rather than "rendered, not agreed".
    // Paired with preserve={false} on the Form.Item, which drops the key again if
    // a display condition later hides the field.
    if (field.fieldType === "disclosure" && values[fieldName] === undefined) {
      form.setFieldsValue({ [fieldName]: false });
    }
  }, [loaded]);

  const handleAddressSelect = useCallback(
    (address: string, addressFields?: AddressFields, isPopulated = false) => {
      const updatedValues = {
        [fieldName]: addressFields?.fullAddress || address,
        [`${fieldName}__${addressKeySuffix}`]:
          (isPopulated && addressFields?.address) || address,
        [`${fieldName}__${apartmentKeySuffix}`]:
          (isPopulated && addressFields?.subpremise) || "",
        [`${fieldName}__${cityKeySuffix}`]:
          (isPopulated && addressFields?.locality) || "",
        [`${fieldName}__${stateKeySuffix}`]:
          (isPopulated && addressFields?.state) || "",
        [`${fieldName}__${zipKeySuffix}`]:
          (isPopulated && addressFields?.postcode) || "",
      };
      form.setFieldsValue(updatedValues);
      form.setFieldsValue({});
    },
    [fieldName]
  );

  const handleAddressClear = useCallback(() => {
    const clearedValues = {
      [fieldName]: undefined,
      [`${fieldName}__${addressKeySuffix}`]: undefined,
      [`${fieldName}__${apartmentKeySuffix}`]: undefined,
      [`${fieldName}__${cityKeySuffix}`]: undefined,
      [`${fieldName}__${stateKeySuffix}`]: undefined,
      [`${fieldName}__${zipKeySuffix}`]: undefined,
    };
    form.setFieldsValue(clearedValues);
  }, [fieldName, form]);

  // Memoized callback for privacyMaskedInput validation changes
  const handlePrivacyMaskedValidationChange = useCallback(
    (isValid: boolean) => {
      const wasValid = confirmationValidRef.current;
      confirmationValidRef.current = isValid;
      // Directly set validation errors using form.setFields
      if (!isValid) {
        form.setFields([
          {
            name: fieldName,
            errors: ["Please confirm your entry"],
          },
        ]);
      } else if (!wasValid) {
        // Confirmation just transitioned invalid -> valid: re-run validation to
        // clear the confirmation error. We deliberately do NOT validate when
        // validity is unchanged (fires on every keystroke), otherwise the regex
        // rule would run instantly instead of waiting for onBlur/onSubmit.
        form.validateFields([fieldName]);
      }
      // Manually trigger disable check since confirmation input isn't a form field
      if (checkDisabled) {
        checkDisabled(form.getFieldsValue());
      }
    },
    [fieldName, form, checkDisabled]
  );

  // Helper function that retrieves the value of a field, either from the current step (using form data) or from a previous step's application response.
  const getFieldValue = (fieldKey: string, stepSlug?: string): any => {
    // Cross-step reference: find the target step's response
    if (stepSlug) {
      const targetResponse = application?.responses?.find(
        (response: any) => response.slug === stepSlug
      );
      return targetResponse?.fields?.[fieldKey];
    }

    // Current step reference: use form data
    const formData = form.getFieldsValue();
    return formData[fieldKey];
  };

  const inputField = () => {
    switch (field.fieldType) {
      case "address":
        return (
          <>
            <Form.Item
              name={fieldName}
              required
              className="ao-bl-select"
              validateTrigger={["onBlur", "onSubmit"]}
              rules={[
                () => ({
                  validator(_, value) {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }
                    // Get the hidden field values to check if address was properly selected
                    const addressValue = form.getFieldValue(
                      `${fieldName}__${addressKeySuffix}`
                    );
                    const cityValue = form.getFieldValue(
                      `${fieldName}__${cityKeySuffix}`
                    );
                    const stateValue = form.getFieldValue(
                      `${fieldName}__${stateKeySuffix}`
                    );
                    const zipValue = form.getFieldValue(
                      `${fieldName}__${zipKeySuffix}`
                    );

                    // If any required component is missing, address wasn't properly selected from list
                    if (
                      !addressValue ||
                      !cityValue ||
                      !stateValue ||
                      !zipValue
                    ) {
                      return Promise.reject(
                        new Error(
                          "Please select an address from the dropdown list"
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <AddressAutocomplete
                placeholder={field.fieldPlaceholder}
                onAddressSelect={handleAddressSelect}
                onClear={handleAddressClear}
                allowClear={true}
                icon={<HomeOutlined />}
                className="ao-bl-select"
                useFullAddress={true}
              />
            </Form.Item>
            <Form.Item
              name={`${fieldName}__${addressKeySuffix}`}
              style={{ display: "none" }}
            />
            <Form.Item
              name={`${fieldName}__${apartmentKeySuffix}`}
              style={{ display: "none" }}
            />
            <Form.Item
              name={`${fieldName}__${cityKeySuffix}`}
              style={{ display: "none" }}
            />
            <Form.Item
              name={`${fieldName}__${stateKeySuffix}`}
              style={{ display: "none" }}
            />
            <Form.Item
              name={`${fieldName}__${zipKeySuffix}`}
              style={{ display: "none" }}
            />
          </>
        );

      case "routingNumber":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            validateTrigger={["onBlur", "onSubmit"]}
            // Use antd rules to display error message.
            rules={[
              () => ({
                // Use antd's validator to get value of field
                validator(_, value) {
                  // Return no validation errors for an empty input.
                  if (value.length === 0) {
                    return Promise.resolve();
                  }
                  // Return error without a message if input contains any underscores.
                  if (value.includes("_")) {
                    return Promise.reject();
                  }
                  // Return error with message if the value is filled out but not a valid routing number.
                  if (!isValidRoutingNumber(value)) {
                    return Promise.reject(
                      new Error("Invalid routing number. Please try again.")
                    );
                  }
                  // If fieldRegex is provided, also validate against the regex pattern
                  if (field.fieldRegexValidation?.regex) {
                    const regex = new RegExp(field.fieldRegexValidation.regex);
                    if (!regex.test(value)) {
                      return Promise.reject(
                        new Error(
                          field.fieldRegexValidation.message ||
                            "Invalid routing number format."
                        )
                      );
                    }
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <MaskedInput
              placeholder={field.fieldPlaceholder}
              mask="000000000"
              defaultValue={field.fieldDefaultValue}
              className="ao-bl-input mt-2"
              disabled={readonly}
            />
          </Form.Item>
        );
      case "cloudinary": {
        const clConfig = field?.fieldConfiguration?.cloudinary;
        return (
          <CloudinaryUpload
            fieldName={fieldName}
            form={form}
            className={"ph-4 mv-6"}
            cloudName={clConfig?.cloudName}
            apiKey={clConfig?.apiKey}
            uploadPreset={clConfig?.uploadPreset}
            required={field.fieldRequired}
            clientAllowedFormats={clConfig?.clientAllowedFormats}
          />
        );
      }
      case "dollarInput":
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <InputNumber
              id={fieldName}
              className="w-100 ao-bl-input"
              min={field.fieldMin || 0}
              max={field.fieldMax}
              placeholder={field.fieldPlaceholder}
              stringMode={true}
              step="0.01"
              precision={field.precision || 2} //Rounds to nearest second decimal place.
              prefix={<DollarCircleOutlined className="site-form-item-icon" />}
              defaultValue={field.fieldDefaultValue}
              formatter={(value) => {
                if (!value) {
                  return "";
                }
                return typeof value === "number"
                  ? formatNumber(value)
                  : formatNumber(parseFloat(value));
              }}
              controls={false}
              disabled={readonly}
            />
          </Form.Item>
        );
      case "inputNumber":
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <InputNumber
              id={fieldName}
              className="w-100 ao-bl-input"
              min={field.fieldMin || 0}
              max={field.fieldMax}
              placeholder={field.fieldPlaceholder}
              stringMode={true}
              step="1"
              defaultValue={field.fieldDefaultValue}
              disabled={readonly}
              precision={field.precision || 0}
              {...field.fieldProps}
            />
          </Form.Item>
        );
      case "checkbox":
        return (
          <div className="flex">
            <Form.Item
              name={fieldName}
              valuePropName="checked"
              className="mr-3 mb-6"
            >
              <Checkbox className="ao-lg-checkbox" />
            </Form.Item>
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4 mt-1 mb-6`}
                dangerouslySetInnerHTML={{
                  __html: field.fieldText ?? "",
                }}
              />
            </div>
          </div>
        );
      case "disclosure": {
        // Managed disclosure: resolve to the public acquire-api proxy, opened in a
        // new tab. Falsy until the application id and slug are both available.
        const disclosureUrl = disclosureDocumentUrl(
          application?.id,
          field.financialDisclosureSlug
        );
        const requireOpen = field.requireOpen ?? false;
        const disclosureFieldKey = field.fieldKey;
        const hasOpenableDoc = !!disclosureUrl;
        // An already-agreed box counts as opened. Read the live form value rather
        // than a mount-time snapshot: on a resumed draft the value arrives from
        // the parent's prefill after this component has mounted, so a one-shot
        // check would leave the box rendered checked but permanently disabled.
        const alreadyAgreed = isTruthy(currentValue);
        // Only gate the checkbox when there is actually a document to open. Gating
        // with no openable link would strand the applicant with a permanently
        // disabled box and no way to satisfy it (a missing slug, or a URL the proxy
        // can't build). Leave it enabled and surface the misconfiguration instead.
        const agreeDisabled =
          hasOpenableDoc && requireOpen && !disclosureOpened && !alreadyAgreed;
        if (requireOpen && !hasOpenableDoc) {
          console.error(
            `Disclosure field "${field.fieldKey}" has requireOpen set but no ` +
              "openable document (missing or unresolved financialDisclosureSlug). " +
              "Leaving the checkbox enabled so the applicant is not stranded."
          );
        }

        // A disclosure cannot record provenance without a step slug. The
        // custom-collections renderer (SectionedCustomFieldGrid) mounts
        // CustomField without one, which is why boa-settings restricts disclosures
        // to single-record steps. That guard is config-time only, so shout here if
        // a config slips past it: silently rendering an agreeable checkbox that
        // records nothing is the exact failure this feature exists to prevent.
        if (!stepSlug) {
          console.error(
            `Disclosure field "${field.fieldKey}" rendered without a stepSlug. ` +
              "No disclosure event can be recorded. Disclosures are only " +
              "supported on single-record custom steps."
          );
        }

        // Fire-and-forget "viewed" event, unlock the checkbox, and re-run the
        // submit gate (opened is not a form field). We don't call preventDefault,
        // so the anchor opens the new tab in the same user gesture. The agreed/
        // declined decision is fired after the step save returns (see the custom
        // step), from the checkbox value that ride the normal save.
        const handleDisclosureOpen = () => {
          setDisclosureOpened(true);
          if (application?.id) {
            recordDisclosureEvent(application.id, {
              financialDisclosureSlug: field.financialDisclosureSlug,
              eventType: "viewed",
              meta: { stepSlug, fieldKey: disclosureFieldKey },
            });
          }
          if (checkDisabled) {
            checkDisabled(form.getFieldsValue());
          }
        };

        return (
          <div className="mb-6">
            {disclosureUrl && (
              <a
                href={disclosureUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDisclosureOpen}
                className="primary f-4 fwb pointer db mb-3"
              >
                <FileTextOutlined className="mr-2" />
                View disclosure document
              </a>
            )}
            <div className="flex">
              <Form.Item
                name={fieldName}
                valuePropName="checked"
                className="mr-3 mb-0"
                // Drop the value from the form store when a display condition
                // hides this field, so it leaves the payload entirely instead of
                // submitting a stale `false` that would read as a declination.
                preserve={false}
              >
                <Checkbox className="ao-lg-checkbox" disabled={agreeDisabled} />
              </Form.Item>
              <div>
                <p
                  className={`${styles.textLinks} f-4 gray-8 lh-4 mt-1 mb-0`}
                  dangerouslySetInnerHTML={{
                    __html: field.fieldText ?? "",
                  }}
                />
              </div>
            </div>
            {agreeDisabled && (
              <p className="f-2 mt-2 mb-0" style={{ color: "#ff4d4f" }}>
                Please open the disclosure document above before you can agree.
              </p>
            )}
          </div>
        );
      }
      case "dateInput":
        return (
          <>
            {/* Visible MaskedInput for user interaction */}
            {/* mb-5 applies 24px margin-bottom, which is the same as default form.item */}
            <div className="mb-5">
              <MaskedInput
                placeholder="MM/DD/YYYY"
                mask="00/00/0000"
                className="ao-bl-input"
                allowClear={false}
                disabled={readonly}
                defaultValue={
                  form.getFieldValue(fieldName)
                    ? dayjs(
                        form.getFieldValue(fieldName),
                        "YYYY-MM-DD",
                        true
                      ).format("MM/DD/YYYY")
                    : ""
                }
                onFocus={(e) => {
                  // Fix Safari cursor positioning - runs after library's cursor positioning
                  const input = e.target as HTMLInputElement;
                  if (input.value.includes("_")) {
                    requestAnimationFrame(() => {
                      input.setSelectionRange(0, 0);
                    });
                  }
                }}
                onChange={(e) => {
                  const formattedValue = e.target.value;
                  // If the input is not fully filled out, don't update the form value or update.
                  // * This freezes the Form.Item at its last completed value.
                  if (formattedValue.includes("_")) {
                    setErrorMessage(null);
                    return;
                  }
                  const parsedDate = dayjs(formattedValue, "MM/DD/YYYY", true);
                  // Check if the parsed date is valid
                  if (!parsedDate.isValid()) {
                    setErrorMessage("Invalid date");
                    form.setFieldsValue({
                      [fieldName]: undefined,
                    });
                    // Call onChange to update disabled state of submit button
                    onChange && onChange({}, form.getFieldsValue());
                    return;
                  }
                  // Clear error message if date is valid
                  setErrorMessage(null);
                  form.setFieldsValue({
                    [fieldName]: parsedDate.format("YYYY-MM-DD"),
                  });
                  // If a onChange function is provided, call it with the current form values.
                  // * This is necessary to update the disabled state of the submit button.
                  onChange && onChange({}, form.getFieldsValue());
                  // Delay validation slightly to avoid immediate reset
                  setTimeout(() => {
                    form.validateFields([fieldName]);
                  }, 0);
                }}
              />
              {/* Error message (if any) */}
              <p className="mb-0 warning-antd">{errorMessage}</p>
            </div>

            {/* Hidden Form.Item to hold the transformed date in YYYY-MM-DD format */}
            <Form.Item
              name={fieldName}
              hidden
              rules={[
                {
                  validator(_, value) {
                    const dayjsValue = dayjs(value);
                    if (!value || !dayjsValue.isValid()) {
                      return Promise.reject();
                    }
                    const minDate = getDate(
                      field.fieldConfiguration?.date?.minDate
                    );
                    const maxDate = getDate(
                      field.fieldConfiguration?.date?.maxDate
                    );

                    if (minDate && dayjs(value).isBefore(minDate)) {
                      const errorMessage = `
                        You must select a date after ${dayjs(minDate).format(
                          "MM/DD/YYYY"
                        )}`;
                      setErrorMessage(errorMessage);
                      return Promise.reject(new Error(errorMessage));
                    }
                    if (maxDate && dayjs(value).isAfter(maxDate)) {
                      const errorMessage = `
                        You must select a date before ${dayjs(maxDate).format(
                          "MM/DD/YYYY"
                        )}`;
                      setErrorMessage(errorMessage);
                      return Promise.reject(new Error(errorMessage));
                    }
                    // - VALIDATION CHECKS AGAINST OTHER INPUTS (CURRENT STEP OR CROSS-STEP) -
                    const validationOptions =
                      field.fieldConfiguration?.date?.validation;

                    // Get beforeField value using separate key and slug properties:
                    // - beforeFieldKey: "fieldName" (required)
                    // - beforeStepSlug: "step-name" (optional, if not provided uses current step)
                    const beforeFieldValue = validationOptions?.beforeFieldKey
                      ? getFieldValue(
                          validationOptions.beforeFieldKey,
                          validationOptions.beforeStepSlug
                        )
                      : null;

                    // Get afterField value using separate key and slug properties:
                    // - afterFieldKey: "fieldName" (required)
                    // - afterStepSlug: "step-name" (optional, if not provided uses current step)
                    const afterFieldValue = validationOptions?.afterFieldKey
                      ? getFieldValue(
                          validationOptions.afterFieldKey,
                          validationOptions.afterStepSlug
                        )
                      : null;
                    const beforeFieldDate = beforeFieldValue
                      ? dayjs(beforeFieldValue)
                      : null;
                    const afterFieldDate = afterFieldValue
                      ? dayjs(afterFieldValue)
                      : null;

                    // ***** Check that provided date is not earlier than a specified input *****
                    if (
                      beforeFieldDate &&
                      dayjsValue.isBefore(beforeFieldDate)
                    ) {
                      const errorMessage = `You must select a date on or after ${beforeFieldDate.format(
                        "MM/DD/YYYY"
                      )}`;
                      setErrorMessage(errorMessage);
                      return Promise.reject(new Error(errorMessage));
                    }
                    // ***** Check that provided date is not later than a specified input *****
                    if (afterFieldDate && dayjsValue.isAfter(afterFieldDate)) {
                      const errorMessage = `You must select a date on or before ${afterFieldDate.format(
                        "MM/DD/YYYY"
                      )}`;
                      setErrorMessage(errorMessage);
                      return Promise.reject(new Error(errorMessage));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />
          </>
        );

      case "date":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            rules={[
              () => ({
                // Use antd's validator to get value of field
                validator(_, date) {
                  // Return no validation errors for an empty input
                  if (!date) {
                    return Promise.resolve();
                  }
                  const validationOptions =
                    field.fieldConfiguration?.date?.validation;

                  // - VALIDATION CHECKS AGAINST SPECIFIED DATES IN JSON CONFIG -
                  // ***** Check that provided date is not earlier than a specified date *****
                  if (
                    validationOptions?.beforeDate &&
                    date.isBefore(dayjs(validationOptions.beforeDate))
                  ) {
                    return Promise.reject(
                      new Error(
                        `You must select a date after ${dayjs(
                          validationOptions.beforeDate
                        ).format("MM/DD/YYYY")}.`
                      )
                    );
                  }
                  // ***** Check that provided date is not later than a specified date *****
                  if (
                    validationOptions?.afterDate &&
                    date.isAfter(dayjs(validationOptions.afterDate))
                  ) {
                    return Promise.reject(
                      new Error(
                        `You must select a date before ${dayjs(
                          validationOptions.afterDate
                        ).format("MM/DD/YYYY")}.`
                      )
                    );
                  }

                  // - VALIDATION CHECKS AGAINST OTHER INPUTS (CURRENT STEP OR CROSS-STEP) -
                  // Get beforeField value using separate key and slug properties:
                  // - beforeFieldKey: "fieldName" (required)
                  // - beforeStepSlug: "step-name" (optional, if not provided uses current step)
                  const beforeFieldValue = validationOptions?.beforeFieldKey
                    ? getFieldValue(
                        validationOptions.beforeFieldKey,
                        validationOptions.beforeStepSlug
                      )
                    : null;

                  // Get afterField value using separate key and slug properties:
                  // - afterFieldKey: "fieldName" (required)
                  // - afterStepSlug: "step-name" (optional, if not provided uses current step)
                  const afterFieldValue = validationOptions?.afterFieldKey
                    ? getFieldValue(
                        validationOptions.afterFieldKey,
                        validationOptions.afterStepSlug
                      )
                    : null;
                  const beforeFieldDate = beforeFieldValue
                    ? dayjs(beforeFieldValue)
                    : null;
                  const afterFieldDate = afterFieldValue
                    ? dayjs(afterFieldValue)
                    : null;

                  // ***** Check that provided date is not earlier than a specified input *****
                  if (beforeFieldDate && date.isBefore(beforeFieldDate)) {
                    return Promise.reject(
                      new Error(
                        `You must select a date on or after ${beforeFieldDate.format(
                          "MM/DD/YYYY"
                        )}`
                      )
                    );
                  }
                  // ***** Check that provided date is not later than a specified input *****
                  if (afterFieldDate && date.isAfter(afterFieldDate)) {
                    return Promise.reject(
                      new Error(
                        `You must select a date on or before ${afterFieldDate.format(
                          "MM/DD/YYYY"
                        )}`
                      )
                    );
                  }

                  // If all these checks pass, the entry is valid.
                  return Promise.resolve();
                },
              }),
            ]}
          >
            {field.fieldDefaultValue ? (
              <DatePicker
                className="w-100 ao-bl-picker"
                config={field?.fieldConfiguration?.date}
                disabled={readonly}
                // @ts-ignore
                minDate={getDate(field?.fieldConfiguration?.date?.minDate)}
                maxDate={getDate(field?.fieldConfiguration?.date?.maxDate)}
                defaultValue={dayjs(
                  field.fieldDefaultValue as string,
                  "MM/DD/YYYY"
                )}
                {...(field.fieldProps &&
                  convertFunctionStrings(field.fieldProps))}
              />
            ) : (
              <DatePicker
                className="w-100 ao-bl-picker"
                config={field?.fieldConfiguration?.date}
                minDate={getDate(field?.fieldConfiguration?.date?.minDate)}
                maxDate={getDate(field?.fieldConfiguration?.date?.maxDate)}
                // @ts-ignore
                defaultPickerValue={
                  field.fieldConfiguration?.date?.defaultPickerDate &&
                  dayjs(field.fieldConfiguration?.date?.defaultPickerDate)
                }
                {...(field.fieldProps &&
                  convertFunctionStrings(field.fieldProps))}
              />
            )}
          </Form.Item>
        );
      case "maskedInput":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            validateTrigger={["onBlur", "onSubmit"]}
            rules={[
              () =>
                antdValidateRegex(
                  field.fieldRegexValidation?.regex,
                  field.fieldRegexValidation?.message,
                  true
                ),
            ]}
          >
            <MaskedInput
              placeholder={field.fieldPlaceholder}
              mask={field.fieldMask?.replaceAll("1", "0") || ""}
              defaultValue={field.fieldDefaultValue}
              className="ao-bl-input mt-2"
              disabled={readonly}
            />
          </Form.Item>
        );
      case "multiselect":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            rules={[
              {
                required: field.fieldRequired,
                message: "Please select at least one option.",
              },
            ]}
          >
            <Checkbox.Group
              className={`w-100${
                !field.inline || !field.fieldConfiguration?.multiselect?.inline
                  ? " ao-vrt-checkbox"
                  : ""
              }`}
              options={(field.fieldOptions ?? []).map((option) => ({
                label: option.label || option.text,
                value: option.value,
              }))}
              disabled={readonly}
            />
          </Form.Item>
        );
      case "ssn":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            // Use antd rules to display error message.
            getValueFromEvent={(value: string) => value}
            rules={[
              () => ({
                // Use antd's validator to get value of field
                validator(_, value) {
                  if (
                    value && // If the value is not empty
                    !isValidSsn(value) && // ...if it isn't a valid ssn
                    value.length === 11 // ...and the user has finished entering SSN.
                  ) {
                    return Promise.reject(
                      new Error(
                        "Invalid social security number. Please try again."
                      )
                    );
                  }
                  if (!confirmationValidRef.current) {
                    return Promise.reject(new Error(SSN_ERROR_MESSAGE));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <SSNMaskedInput
              placeholder={field.fieldPlaceholder || "•••-••-••••"}
              className="ao-bl-input mt-2"
              disabled={readonly}
              requireConfirmation={field.requireConfirmation}
              confirmationClassName="ao-bl-input"
              onValidationChange={(isValid) => {
                confirmationValidRef.current = isValid;
                // Directly set validation errors using form.setFields
                if (!isValid) {
                  form.setFields([
                    {
                      name: fieldName,
                      errors: [SSN_ERROR_MESSAGE],
                    },
                  ]);
                } else {
                  // Clear confirmation errors, but keep other validation errors
                  form.validateFields([fieldName]);
                }
                // Manually trigger disable check since confirmation input isn't a form field
                if (checkDisabled) {
                  checkDisabled(form.getFieldsValue());
                }
              }}
            />
          </Form.Item>
        );
      case "privacyMaskedInput":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            validateTrigger={["onBlur", "onSubmit"]}
            getValueFromEvent={(value: string) => value}
            rules={[
              () => ({
                validator(_, value) {
                  // Run the regex whenever a value is present (no length gate),
                  // so incomplete entries are caught. Only fires onBlur/onSubmit.
                  if (
                    value && // If the value is not empty
                    field.fieldRegexValidation?.regex // ...and regex validation is provided
                  ) {
                    const regex = new RegExp(field.fieldRegexValidation.regex);
                    if (!regex.test(value)) {
                      return Promise.reject(
                        new Error(
                          field.fieldRegexValidation.message ||
                            "Invalid format. Please try again."
                        )
                      );
                    }
                  }
                  // Check confirmation validity
                  if (!confirmationValidRef.current) {
                    return Promise.reject(
                      new Error("Please confirm your entry")
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <PrivacyMaskedInput
              mask={field.fieldMask}
              placeholder={field.fieldPlaceholder}
              className="ao-bl-input mt-2"
              disabled={readonly}
              requireConfirmation={field.requireConfirmation}
              confirmationClassName="ao-bl-input"
              onValidationChange={handlePrivacyMaskedValidationChange}
            />
          </Form.Item>
        );
      case "autocomplete":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            validateTrigger={"onBlur"}
            rules={[
              () => ({
                // Use antd's validator to get value of field.
                // * This validation check will be ignored if "allowFreeInput" is marked true.
                validator(_, value) {
                  if (
                    !field.fieldConfiguration?.autocomplete?.allowFreeInput &&
                    !isValidAutoComplete(value, field.fieldOptions) &&
                    value.length > 0
                  ) {
                    return Promise.reject(
                      new Error(
                        "Please select an option from the list (then change fields to clear this warning)"
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <AutoComplete
              options={field.fieldOptions ?? []}
              placeholder={field.fieldPlaceholder}
              defaultValue={field.fieldDefaultValue}
              defaultActiveFirstOption={true}
              className="ao-bl-select"
              disabled={readonly}
              filterOption={(input, options) => {
                if (!options?.value || !options.label) {
                  return false;
                }
                return (
                  options.value
                    .toString()
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0 ||
                  options.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
            />
          </Form.Item>
        );
      case "select":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            className="pointer"
          >
            <Select
              showSearch
              optionLabelProp="label" // Display the selection by the label, not the value
              filterOption={(input, options) => {
                if (!options?.value || !options.label) {
                  return false;
                }
                return (
                  options.value
                    .toString()
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0 ||
                  options.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              options={field.fieldOptions ?? []}
              notFoundContent={"No options found"}
              placeholder={field.fieldPlaceholder}
              defaultValue={field.fieldDefaultValue}
              className="ao-bl-select"
              disabled={readonly}
            />
          </Form.Item>
        );
      /**
       * @deprecated The 'slider' field type is deprecated and will be removed in future releases.
       */
      case "slider":
        if (!field.fieldMin || !field.fieldMax) {
          return <></>;
        }
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <SliderWithDisplay
              key={fieldName}
              min={field.fieldMin}
              max={field.fieldMax}
              textColor={brand.primaryColor}
              sliderColor={brand.primaryColor}
            />
          </Form.Item>
        );
      case "radio":
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <Radio.Group>
              {field?.fieldOptions.map((option: any, index) => (
                <div className="flex" key={option.value + index}>
                  <Radio
                    key={option.value}
                    value={option.value.toString()} // radio values must always be strings.
                    className="pv-2 ao-lg-radio"
                    disabled={readonly}
                  >
                    <div
                      className="fwn gray-8"
                      dangerouslySetInnerHTML={{
                        __html: option.text ?? "",
                      }}
                    />
                  </Radio>
                </div>
              ))}
            </Radio.Group>
          </Form.Item>
        );
      case "email":
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <Input
              placeholder={field.fieldPlaceholder}
              type="email"
              defaultValue={field.fieldDefaultValue}
              className="ao-bl-input"
              disabled={readonly}
            />
          </Form.Item>
        );
      case "phone":
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            // required={false} // Requirement is handled by our own validator... not Antd's.
            rules={[
              () => ({
                // Use antd's validator to get value of field
                validator(_, value) {
                  if (
                    value &&
                    !isValidPhoneNumber(value.toString(), "US") && // if it isn't a valid phone number.
                    value.slice(-1) !== "_" // ...and it is filled out (the last character isn't a "_")
                  ) {
                    return Promise.reject(
                      new Error(
                        "Invalid number. Please try again or use a new number."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <MaskedInput
              placeholder={
                field.fieldPlaceholder &&
                formatAsNationalPhoneNum(field.fieldPlaceholder)
              }
              mask="(000) 000-0000"
              className="ao-bl-input"
              disabled={readonly}
            />
          </Form.Item>
        );
      case "textarea": {
        const { TextArea } = Input;
        const taConfig = field.fieldConfiguration?.textarea;
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            <TextArea
              placeholder={field.fieldPlaceholder}
              defaultValue={field.fieldDefaultValue}
              autoSize={{
                minRows: taConfig?.minRows || 2,
                maxRows: taConfig?.maxRows || 5,
              }}
              showCount={taConfig?.showCount || true}
              maxLength={taConfig?.maxLength || 400}
              className="ao-textarea mt-1 bg-white gray-8 ba b-gray-8"
              style={{ fontSize: "18px", boxShadow: "none" }}
              disabled={readonly}
            />
          </Form.Item>
        );
      }
      /**
       * @deprecated The 'membershipEligibility' field type is deprecated and will be removed in future releases.
       */
      case "membershipEligibility": {
        // Sort the eligibilities by ascending sortOrder.
        const sortedEligibilities = [
          ...(flow?.membershipEligibilities ?? []),
        ].sort((a, b) => a.sortOrder - b.sortOrder);

        // Determine the input type based on the field configuration. Default to "select" if no configuration is provided or if field type is not "radio".
        const isRadio =
          field.fieldConfiguration?.membershipEligibility?.fieldType ===
          "radio";

        // Create radio options if the input type is radio.
        const createRadioOptions = (eligibilities: MembershipEligibility[]) =>
          eligibilities.map((eligiblity) => (
            <div key={eligiblity.id} className="flex">
              <Radio
                value={eligiblity.id}
                className="pv-2 ao-lg-radio"
                disabled={readonly}
              >
                <div
                  className="fwn gray-8"
                  dangerouslySetInnerHTML={{
                    __html: eligiblity.description ?? "",
                  }}
                />
              </Radio>
            </div>
          ));

        // Create select options if the input type is not radio.
        const createSelectOptions = (eligibilities: MembershipEligibility[]) =>
          eligibilities.map((eligiblity) => ({
            value: eligiblity.id,
            label: eligiblity.description,
          }));

        // Create the appropriate options based on the input type.
        const options = isRadio
          ? createRadioOptions(sortedEligibilities)
          : createSelectOptions(sortedEligibilities);

        // Return the appropriate input type.
        return (
          <Form.Item name={fieldName} valuePropName={"value"}>
            {isRadio ? (
              <Radio.Group>{options as ReactElement[]}</Radio.Group>
            ) : (
              <Select
                options={options as { value: string; label: string }[]}
                placeholder={field.fieldPlaceholder}
                defaultValue={field.fieldDefaultValue}
                className="ao-bl-select"
                disabled={readonly}
              />
            )}
          </Form.Item>
        );
      }
      case "text":
      default:
        return (
          <Form.Item
            name={fieldName}
            valuePropName={"value"}
            validateTrigger={["onBlur", "onSubmit"]}
            rules={[
              antdValidateRegex(
                field.fieldRegexValidation?.regex,
                field.fieldRegexValidation?.message
              ),
            ]}
          >
            <Input
              placeholder={field.fieldPlaceholder}
              defaultValue={field.fieldDefaultValue}
              className={`ao-bl-input ${readonly && `primary`}`}
              disabled={readonly}
              maxLength={field.maxLength}
            />
          </Form.Item>
        );
    }
  };

  return (
    <Row key={index}>
      <Col xs={24} md={22}>
        {field.fieldLabel ? (
          <label
            htmlFor={fieldName}
            dangerouslySetInnerHTML={{ __html: field.fieldLabel }}
          />
        ) : (
          <label htmlFor={fieldName}>{field.fieldLabel}</label>
        )}{" "}
        {field.fieldSubtitle && (
          <div
            style={{
              fontSize: "0.85rem",
              color: "#9CA3AF",
              fontWeight: 400,
              marginTop: "2px",
            }}
            dangerouslySetInnerHTML={{ __html: field.fieldSubtitle }}
          />
        )}
      </Col>
      {/* Render a full-width component for specified components, but 2/3 width
      for the rest. */}
      <Col
        xs={24}
        sm={FULL_WIDTH_COMPONENTS.includes(field.fieldType) ? 24 : 16}
      >
        {inputField()}
      </Col>
    </Row>
  );
}
